/**
 * Browser session frame stream (Phase 2)
 *
 * A tiny client-side module that subscribes to the same WebSocket
 * stream the AgentActivity page uses (`/ws/:sessionId` on the
 * browser-service host) and exposes:
 *   - getInlineBrowserTileFrame(sessionId): latest data-URL frame
 *     (synchronously) for inline thumbnail rendering.
 *   - subscribeToBrowserSessionFrames(sessionId, cb): subscribe to
 *     incoming frames so the chat tile can re-render when the
 *     browser-service emits a new screenshot.
 *
 * This module deliberately mirrors LiveBrowserView's WS contract but
 * stays decoupled so it can be used inside the chat tile without
 * importing the AgentActivity page tree.
 */

import {
  getBrowserServiceBaseUrl,
  browserServiceWsFromBase,
  getBrowserServiceAuthHeaders,
} from '../../browserService/baseUrl';

const FRAME_CACHE = new Map<string, string>();
const SUBSCRIBERS = new Map<string, Set<(dataUrl: string) => void>>();
const SOCKETS = new Map<string, WebSocket>();
/** sessionIds currently in the middle of an async base-URL resolve+open. */
const PENDING = new Set<string>();

/**
 * Per-session base override (2026-08-18): sign-in desktop sessions can live
 * on the user's ACCOUNT VM (…/browser-api) instead of the platform-shared
 * service. Whoever creates such a session registers its base here so the
 * frame stream connects to the VM that actually hosts it. Unregistered
 * sessions keep the shared-base behavior unchanged.
 */
const SESSION_BASE_OVERRIDES = new Map<string, string>();

export function registerBrowserSessionBase(sessionId: string, base: string | null): void {
  if (!sessionId) return;
  if (base && base.trim()) SESSION_BASE_OVERRIDES.set(sessionId, base.trim().replace(/\/+$/, ''));
  else SESSION_BASE_OVERRIDES.delete(sessionId);
}

export function getRegisteredBrowserSessionBase(sessionId: string): string | null {
  return SESSION_BASE_OVERRIDES.get(sessionId) ?? null;
}

async function wsUrlFor(sessionId: string): Promise<string> {
  const base = SESSION_BASE_OVERRIDES.get(sessionId) ?? (await getBrowserServiceBaseUrl());
  const wsBase = browserServiceWsFromBase(base);
  // 2026-08-11 (P0 security item #6): the browser-service viewer WS now
  // requires `?auth=<token>` when its bearer-token gate is provisioned
  // (browsers cannot set an Authorization header on a WebSocket; the server
  // closes unauthenticated viewers with code 4401). Token comes from the same
  // Firestore SSOT doc as the base URL (field `browserServiceToken`); while
  // unprovisioned this resolves to null and the URL is unchanged — today's
  // behavior, matching the server's allow-all default.
  // The key that belongs to THIS host (S1b-3a, 2026-09-26): an account
  // machine's own registered key, the shared key only for the platform's own
  // shared host, nothing otherwise. Until then this always took the shared
  // internal key, so a session on another organization's machine carried it
  // in the socket's address, where that machine's access log could record it.
  const auth = (await getBrowserServiceAuthHeaders(base)).Authorization ?? '';
  const token = auth.startsWith('Bearer ') ? auth.slice('Bearer '.length) : '';
  const authQs = token ? `?auth=${encodeURIComponent(token)}` : '';
  return `${wsBase}/ws/${sessionId}${authQs}`;
}

function ensureSocket(sessionId: string): void {
  if (typeof window === 'undefined') return;
  if (SOCKETS.has(sessionId) || PENDING.has(sessionId)) return;
  PENDING.add(sessionId);
  void (async () => {
    let url: string;
    try {
      url = await wsUrlFor(sessionId);
    } catch (err) {
      PENDING.delete(sessionId);
      console.warn(
        `[browserSessionFrames] base URL resolve failed: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
      return;
    }
    if (SOCKETS.has(sessionId)) {
      PENDING.delete(sessionId);
      return;
    }
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      PENDING.delete(sessionId);
      console.warn(
        `[browserSessionFrames] WS open failed: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
      return;
    }
    SOCKETS.set(sessionId, ws);
    PENDING.delete(sessionId);
    attachSocketHandlers(sessionId, ws);
  })();
}

function attachSocketHandlers(sessionId: string, ws: WebSocket): void {

  ws.addEventListener('message', (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg.type === 'screenshot' && typeof msg.screenshot === 'string') {
        const dataUrl = msg.screenshot.startsWith('data:')
          ? msg.screenshot
          : `data:image/png;base64,${msg.screenshot}`;
        FRAME_CACHE.set(sessionId, dataUrl);
        const subs = SUBSCRIBERS.get(sessionId);
        if (subs) for (const cb of subs) cb(dataUrl);
      }
    } catch {
      // ignore malformed messages
    }
  });

  ws.addEventListener('close', () => {
    SOCKETS.delete(sessionId);
  });
  ws.addEventListener('error', () => {
    try {
      ws.close();
    } catch {
      // ignore
    }
    SOCKETS.delete(sessionId);
  });
}

export function getInlineBrowserTileFrame(sessionId: string): string | null {
  ensureSocket(sessionId);
  return FRAME_CACHE.get(sessionId) ?? null;
}

export function subscribeToBrowserSessionFrames(
  sessionId: string,
  cb: (dataUrl: string) => void
): () => void {
  ensureSocket(sessionId);
  let set = SUBSCRIBERS.get(sessionId);
  if (!set) {
    set = new Set();
    SUBSCRIBERS.set(sessionId, set);
  }
  set.add(cb);
  // Replay current frame immediately so subscribers don't wait for the
  // next push.
  const cached = FRAME_CACHE.get(sessionId);
  if (cached) cb(cached);
  return () => {
    set!.delete(cb);
    if (set!.size === 0) {
      const sock = SOCKETS.get(sessionId);
      if (sock && sock.readyState <= 1) {
        try {
          sock.close();
        } catch {
          // ignore
        }
      }
      SOCKETS.delete(sessionId);
      SUBSCRIBERS.delete(sessionId);
    }
  };
}
